import PropTypes from 'prop-types';
import React, { lazy, Suspense, Component } from 'react';

import noop from 'lodash/noop';
import Logger from '@sm360/logger-sentry';
import { ShowroomService, Quote, PurchaseMethods, ShowroomAPIConstants } from '@sm360/widget-commons-vehicle';
import { UrlService, LocaleService, ConfigsService } from '@sm360/widget-commons-core-services';
import {
    NoSSR,
    Account,
    AccountService,
    AnalyticsService,
    LoaderFullScreen,
    VIRTUAL_PAGEVIEWS as PAGES,
} from '@sm360/widget-commons-general';
import { Wizard } from '@sm360/widget-commons-forms';

import LoginStep from 'trade-in-360/components/LoginStep/LoginStep.jsx';
import VehicleForm from 'trade-in-360/components/VehicleForm/VehicleForm.jsx';
import ConfirmationStep from 'trade-in-360/components/ConfirmationStep/ConfirmationStep.jsx';

import TradeInService from 'trade-in-360/services/TradeInService/TradeInService.js';
import OfficialTradeInForm from 'trade-in-360/components/OfficialTradeInForm/OfficialTradeInForm.jsx';
import TradeInModel from 'trade-in-360/model/TradeInModel.js';

import 'trade-in-360/components/TradeInWrapper/TradeInWrapper.scss';
import NewTradeInContext from '../../NewTradeInContext';

// The webpackChunkName name here is intended to be public. Webpack will use it to name files in production and development modes.
const CanadianBlackBookEstimate = lazy(() =>
    import(/* webpackChunkName: "CanadianBlackBookEstimate" */ 'trade-in-360/components/CanadianBlackBookEstimate/CanadianBlackBookEstimate.jsx')
);

const { PurchaseMethodsActions } = PurchaseMethods;

class TradeInWrapper extends Component {
    constructor(props) {
        super(props);

        this.state = {
            isScreenLock: false,
            account: props.account,
            quote: props.quote,
            tradeInVehicle: {
                id: '',
                make: '',
                model: '',
                trim: '',
                style: '',
                year: '',
                exteriorColor: '',
                odometer: '',
            },
            tradeInValues: {
                low: 0,
                high: 0,
                amount: 0,
            },
        };

        this.loadQuote = this.loadQuote.bind(this);
        this.toggleScreenLock = this.toggleScreenLock.bind(this);
        this.onLoginStepDone = this.onLoginStepDone.bind(this);
        this.allStepCompleted = this.allStepCompleted.bind(this);
        this.updateQuoteRevision = this.updateQuoteRevision.bind(this);
        this.updateTradeInVehicle = this.updateTradeInVehicle.bind(this);

        this.handleCBBEstimateChange = this.handleCBBEstimateChange.bind(this);

        this.logger = new Logger(this.constructor.displayName, ConfigsService.getLoggerContext());
    }

    componentDidMount() {
        this.loadQuote();
        if (this.props.isOfficialTradeIn) {
            AnalyticsService.virtualPageView(PAGES.OFFICIAL_TRADEIN());
        }
    }

    onLoginStepDone(account) {
        if (account) {
            this.setState({ account });
        }
    }

    getSteps() {
        const steps = [];

        steps.push(({ goNextStep }) => (
            <LoginStep
                account={this.state.account}
                stepDone={(account) => {
                    this.onLoginStepDone(account);
                    goNextStep();
                }}
                isRenderedInModal={this.props.isRenderedInModal}
            />
        ));

        steps.push(({ goNextStep }) => (
            <VehicleForm
                isRenderedInModal={this.props.isRenderedInModal}
                lockScreen={this.toggleScreenLock}
                quote={this.state.quote}
                onFormSubmit={(data) => this.setState(data, goNextStep)}
                onTradeInVehicleUpdated={this.updateTradeInVehicle}
                useCBB={this.props.useCBB}
                isOfficialTradeIn={this.props.isOfficialTradeIn}
                tradeInVehicle={this.state.tradeInVehicle}
                loadQuote={this.loadQuote}
                config={this.props.config}
            />
        ));

        if (!this.props.isOfficialTradeIn && this.props.useCBB) {
            steps.push(({ goPreviousStep, goNextStep }) => (
                <NoSSR>
                    <Suspense fallback="">
                        <CanadianBlackBookEstimate
                            tradeInVehicle={this.state.tradeInVehicle}
                            goToOfficialTradeInForm={goNextStep}
                            backToVehicleForm={goPreviousStep}
                            tradeInValues={this.state.tradeInValues}
                            handleEstimateChange={this.handleCBBEstimateChange}
                            isOfficialTradeInActivated
                            isRenderedInModal={this.props.isRenderedInModal}
                            config={this.props.config}
                        />
                    </Suspense>
                </NoSSR>
            ));
        }

        steps.push(({ goNextStep }) => (
            <OfficialTradeInForm
                quote={this.state.quote}
                updateQuoteRevision={this.updateQuoteRevision}
                onFormCompletion={goNextStep}
                tradeInValues={this.state.tradeInValues}
                tradeInVehicle={this.state.tradeInVehicle}
                tradeInId={this.state.tradeInVehicle.id}
                leadId={this.state.leadId}
                loadQuote={this.loadQuote}
                config={this.props.config}
            />
        ));

        steps.push(() => <ConfirmationStep onTradeInSuccess={() => this.props.onTradeInSuccess(this.state.quote)} />);

        return steps;
    }
    static contextType = NewTradeInContext;
    updateQuoteRevision(updatedQuotes) {
        if (this.state.quote) {
            const updatedQuote = updatedQuotes.find((quote) => quote.id === this.state.quote.id);
            if (updatedQuote) {
                this.setState((prevState) => ({
                    quote: prevState.quote.set('revision', updatedQuote.__revision || updatedQuote.revision),
                }));
            }
        }
    }

    updateTradeInVehicle(tradeInVehicle, onStateSet) {
        this.setState((oldState) => {
            return { ...oldState, tradeInVehicle };
        }, onStateSet);
    }

    toggleScreenLock() {
        this.setState((prevState) => {
            return { isScreenLock: !prevState.isScreenLock };
        });
    }

    loadQuote() {
        const quoteId = UrlService.getUrlParameter('quoteId');
        const writeToken = UrlService.getUrlParameter('writeToken');
        if (quoteId) {
            ShowroomService.loadQuote(quoteId, ShowroomAPIConstants.DATA_LEVEL_7, false, this.props.config)
                .then((quote) => {
                    if (writeToken) {
                        this.setState({ quote: quote.set('securityToken', writeToken) });
                    } else {
                        this.setState({ quote });
                    }
                })
                .catch((error) => {
                    this.logger.error('TradeInWrapper#loadQuote error', error);
                });
        }
    }

    handleCBBEstimateChange(tradeInValue) {
        PurchaseMethodsActions.setTradeIn(tradeInValue);

        return TradeInService.fetchTradeIn(this.props.config)
            .then((tradeIn) => {
                const { leadId, id } = tradeIn;
                const tradeInToSave = new TradeInModel({
                    id,
                    leadId,
                    estimation: {
                        amount: tradeInValue,
                        high: this.state.tradeInValues.high,
                        low: this.state.tradeInValues.low,
                    },
                    tradeInVehicle: null, // unset these values like this it's not updated
                    profile: null, // unset these values
                });
                return TradeInService.saveTradeIn(tradeInToSave, this.props.quote, false, this.props.config);
            })
            .then(() => {
                this.loadQuote();
            })
            .catch((e) => {
                this.logger.error('An error happened while saving the showroom tradeIn value', e);
                return Promise.reject(e);
            });
    }

    allStepCompleted() {
        return Object.values(this.state.completedSteps).every((step) => step);
    }

    render() {
        const startingStepIndex = AccountService.isAuthenticated() ? 1 : 0;
        // for some reason the getSteps won't cause the Wizard to render when the state
        // has changed for the step functions
        return (
            <div className="TradeIn__wrapper">
                <LoaderFullScreen show={this.state.isScreenLock} message={LocaleService.t('TRADE_IN.SCREEN_LOCKED')}>
                    <Wizard steps={this.getSteps()} onStepTransition={() => {}} startingStepIndex={startingStepIndex} />
                </LoaderFullScreen>
            </div>
        );
    }
}

TradeInWrapper.defaultProps = {
    quote: null,
    account: null,
    onTradeInSuccess: noop,
    config: {},
};

TradeInWrapper.propTypes = {
    account: PropTypes.instanceOf(Account),
    quote: PropTypes.instanceOf(Quote.QuoteModel),
    useCBB: PropTypes.bool.isRequired,
    isOfficialTradeIn: PropTypes.bool.isRequired,
    isRenderedInModal: PropTypes.bool.isRequired,
    onTradeInSuccess: PropTypes.func,
    config: PropTypes.object,
};

TradeInWrapper.displayName = 'TradeInWrapper';

export default TradeInWrapper;
